import { Container } from "@/components/molecules/Container";
import { Inner } from "@/components/molecules/Inner";
import Careers from "@/components/organisms/Careers";
import Contact from "@/components/organisms/Contact";
import { Footer } from "@/components/templates/Footer";
import { Header } from "@/components/templates/Header";
import Layout from "@/components/templates/Layout";
import Meta from "@/components/templates/Meta";
import { faAngleRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Link from "next/link";
import { useMemo, useState } from "react";

dayjs.extend(utc);
dayjs.extend(timezone);

const ITEMS_PER_PAGE = 20;

const YEAR_FILTERS = ["すべて", "2026", "2025", "2024", "2023以前"];

function removeOshiraseSuffix(title) {
  return title.replace(/\s*の?お知らせ\s*$/, "");
}

function getArticleYear(dateStr) {
  return dayjs.utc(dateStr).tz("Asia/Tokyo").year();
}

// ニュースリリース一覧の取得（microCMS + TWP統合）
export const getStaticProps = async () => {
  const { fetchAllNews } = await import("@/lib/news-api");
  const allNews = await fetchAllNews();

  return {
    props: {
      contents: allNews,
    },
    revalidate: 60,
  };
};

function NewsRow({ article }) {
  const formattedDate = dayjs
    .utc(article.date)
    .tz("Asia/Tokyo")
    .format("YYYY.MM.DD");

  const displayTitle = removeOshiraseSuffix(article.title);

  const hasLink = article.detailflg === true || article.url;
  const href = article.detailflg === true
    ? "/news/article/" + article.id
    : article.url || null;

  const ariaLabel = `${formattedDate} ${displayTitle}`;

  const rowContent = (
    <div className="group flex w-full items-center gap-3 px-4 py-5 sm:gap-6 sm:px-5">
      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-6">
        <time
          dateTime={dayjs.utc(article.date).tz("Asia/Tokyo").format("YYYY-MM-DD")}
          className="shrink-0 text-sm text-txt-light"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {formattedDate}
        </time>
        <span className="min-w-0 flex-1 text-sm leading-[1.6] text-txt-base">
          {displayTitle}
        </span>
      </div>
      {hasLink && (
        <span className="shrink-0 text-xs text-icn transition-colors duration-200 group-hover:text-hov">
          <FontAwesomeIcon icon={faAngleRight} />
        </span>
      )}
    </div>
  );

  if (hasLink && href) {
    return (
      <li className="list-none border-b border-solid border-bdc-light last:border-b-0">
        <Link href={href}>
          <a
            className="block rounded-sm no-underline transition-colors duration-200 hover:bg-bgc-base focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pri"
            aria-label={ariaLabel}
          >
            {rowContent}
          </a>
        </Link>
      </li>
    );
  }

  return (
    <li className="list-none border-b border-solid border-bdc-light last:border-b-0">
      <div className="block rounded-sm">{rowContent}</div>
    </li>
  );
}

export default function NewsPage(props) {
  const articles = props.contents;
  const [activeFilter, setActiveFilter] = useState("すべて");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const filteredArticles = useMemo(() => {
    if (activeFilter === "すべて") return articles;
    if (activeFilter === "2023以前") {
      return articles.filter((a) => getArticleYear(a.date) <= 2023);
    }
    const year = parseInt(activeFilter, 10);
    return articles.filter((a) => getArticleYear(a.date) === year);
  }, [articles, activeFilter]);

  const displayedArticles = filteredArticles.slice(0, visibleCount);
  const hasMore = visibleCount < filteredArticles.length;

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  return (
    <>
      <Meta
        pageTitle={"お知らせ"}
        pageDescription={
          "株式会社トッカシステムズのお知らせです。日進月歩のデジタルテクノロジー分野において『お客様のビジネスに特化（Tocca）したシステムを』という理念を社名に込めて、使える・価値のあるシステムを提供します。"
        }
        pagePath={"https://tocca.systems/news/"}
      />
      <Layout>
        <Header isRelative={true} />
        <main className="flex-1">
          <Container>
            <Inner className="pt-6 pb-12 md:pt-8 md:pb-20">
              <div className="box-border px-6 sm:px-8 md:px-12 lg:px-0">
                {/* ヘッダー */}
                <div className="mb-8 md:mb-12">
                  <p
                    className="mb-2 text-xs font-bold uppercase text-pri"
                    style={{ letterSpacing: "0.2em" }}
                  >
                    NEWS
                  </p>
                  <h1 className="text-2xl font-bold text-txt-base md:text-3xl">
                    お知らせ
                  </h1>
                  <hr className="mt-6 border-t border-bdc-base" />
                </div>

                {/* 年フィルタ */}
                <div
                  className="mb-8 flex flex-wrap gap-2 md:gap-3"
                  role="group"
                  aria-label="年で絞り込み"
                >
                  {YEAR_FILTERS.map((filter) => (
                    <button
                      key={filter}
                      onClick={() => handleFilterChange(filter)}
                      className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pri ${
                        activeFilter === filter
                          ? "border border-solid border-pri bg-pri text-white"
                          : "border border-solid border-bdc-base bg-white text-txt-base hover:border-pri hover:text-pri"
                      }`}
                      aria-pressed={activeFilter === filter}
                    >
                      {filter}
                    </button>
                  ))}
                </div>

                {/* 記事リスト */}
                <div className="overflow-hidden rounded-xl border border-solid border-bdc-light bg-white">
                  <ul className="m-0 p-0">
                    {displayedArticles.map((article) => (
                      <NewsRow key={article.id} article={article} />
                    ))}
                  </ul>
                  {displayedArticles.length === 0 && (
                    <div className="py-12 text-center text-sm text-txt-light">
                      該当するお知らせはありません
                    </div>
                  )}
                </div>

                {/* もっと見る */}
                {hasMore && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={handleLoadMore}
                      className="rounded-full border border-solid border-bdc-base bg-white px-8 py-3 text-sm font-bold text-txt-base transition-colors duration-200 hover:border-pri hover:text-pri focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pri"
                    >
                      もっと見る
                    </button>
                  </div>
                )}
              </div>
            </Inner>
          </Container>
        </main>
        <Careers />
        <Contact />
        <Footer />
      </Layout>
    </>
  );
}
